import React, { useContext, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import {
  NestedRecord,
  triggerConfigData,
  WorkFlowActionContext,
  WorkFlowContext,
  WorkFlowContextType,
  workFlowDataType,
} from '../../../../utils/context';
import {
  timeOptions,
  monthDaysOptions,
  daySelectionOptions,
  timeZoneOptions,
} from '../config/constants';
import {
  getAssistantDetailsInFlow,
  getPreviousNodeActionData,
  getPreviousNodeTriggerData,
  getPreviousResponseKeys,
  postWorkFlowAction,
  postWorkFlowTrigger,
} from '../services';
import makeToast from '../../../../components/toaster';
import BasicModal from '../../../../components/action-modal';
import { validateJSON } from '../../../../services';
import { Fields, TriggerType, WorkflowFields, WorkFlowPanelData } from '../data/workflow-data';
import RenderConfig from '../services/render-config';
import { configureTabProps } from '../automator.props';

function Configure({
  selectedNode,
  configModule,
  setActiveTab,
  setTriggerId,
  setActionId,
  actionId,
  disableMode,
  setLogout,
  activeTab,
  setNodes,
}: configureTabProps) {
  const { workFlowData, setWorkFlowData } = useContext<WorkFlowContextType>(WorkFlowContext);
  const { workFlowActionData, setWorkFlowActionData } = useContext(WorkFlowActionContext);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const refInput = useRef<HTMLInputElement>(null);
  const { workflowId } = useParams();
  const [updatedWorkflowPanelData, setUpdatedWorkflowPanelData] =
    useState<WorkflowFields>(WorkFlowPanelData);
  const [dynamicResponses, setDynamicResponses] = useState<string[]>([]);
  const [selectedValue, setSelectedValue] = useState<NestedRecord>({});
  const triggerType = selectedNode?.data?.trigger_type || '';
  const [openPayloadModal, setOpenPayloadModal] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  const savedValue = localStorage?.getItem('workFlowData');

  const Configurations =
    (configModule as NestedRecord)?.config ||
    (configModule as NestedRecord)?.trigger_config ||
    configModule ||
    (savedValue && JSON.parse(savedValue)?.trigger?.trigger_config);

  const allKeys =
    Configurations && Object.keys(Configurations)?.length > 0 ? Object.keys(Configurations) : [];

  useEffect(() => {
    if (savedValue) {
      const parsedValue = JSON.parse(savedValue!)?.trigger?.workFlowData?.trigger_config;
      if (parsedValue) {
        setSelectedValue(parsedValue);
      }
    }
    if (
      savedValue &&
      triggerType === 'Assistants' &&
      Configurations &&
      Object.keys(Configurations).length
    ) {
      Object.entries(Configurations).map(([key, value]) => ({
        key,
        value,
      }));
    }
  }, [activeTab]);

  useEffect(() => {
    if (
      !JSON.parse(savedValue!)?.trigger?.workFlowData?.trigger_config ||
      (configModule as NestedRecord)?.id === workFlowData?.trigger_catalogue_id
    ) {
      const allOptions = [
        ...timeOptions,
        ...timeZoneOptions,
        ...daySelectionOptions,
        ...monthDaysOptions,
      ];
      const valueToIdMap = Object?.fromEntries(
        allOptions?.map((option) => [option.value, option.id]),
      );
      const initialSelectedValues = allKeys?.reduce((acc, key) => {
        const value = (workFlowData as workFlowDataType & { workFlowData?: workFlowDataType })
          ?.workFlowData?.trigger_config?.[key] as string;
        if (valueToIdMap[value] !== undefined) {
          acc[key] = valueToIdMap[value]; // Map value to id if found
        } else if (typeof value === 'boolean') {
          acc[key] = value;
        } else {
          acc[key] = value || '';
        }

        return acc;
      }, {} as NestedRecord);
      setSelectedValue(initialSelectedValues);
    }
    const currentAssistantId = workFlowActionData?.action_config?.assistant_id;
    if (currentAssistantId) {
      getAssistantDetailsInFlow(Number(currentAssistantId))
        .then((res) => {
          if (res?.status === 200) {
            const data = res.data.data.assistant;
            const mappedPrompt = data?.prompt
              ?.map((item: { content: string }) => {
                const matches = item.content.match(/\{([^}]+)\}/g);
                return matches
                  ? matches.map((match: string) => ({
                      key: match.replace(/[{}]/g, ''), // Remove curly braces from the key
                      value: '',
                    }))
                  : [];
              })
              .flat();
            setUpdatedWorkflowPanelData((prev: WorkflowFields) => {
              return {
                ...(prev ?? []),
                [triggerType]: {
                  ...(prev[triggerType as keyof WorkflowFields] ?? []),
                  configuration: [
                    ...(prev[triggerType as keyof WorkflowFields].configuration ?? []).map(
                      (field: Fields) => {
                        if (field.props.name === 'dynamic_variables') {
                          const dynamicVariables = Object.entries(
                            workFlowActionData.action_config?.dynamic_variables || {},
                          ).map(([key, value]) => ({ key, value }));
                          return {
                            ...field,
                            keyValuePairs: dynamicVariables.length
                              ? dynamicVariables
                              : mappedPrompt, // Use the passed 'options' parameter
                          };
                        }
                        return field;
                      },
                    ),
                  ],
                },
              };
            });
          } else if (res?.status === 401) {
            setLogout(true);
          }
        })
        .catch(() => {
          makeToast('error', 'Something went wrong!');
        });
    }

    if (triggerType === 'API' || triggerType === 'Send HTTP Request') {
      setWorkFlowActionData({
        ...workFlowActionData,
        action_order: selectedNode?.id,
        action_config: {
          ...workFlowActionData?.action_config,
          body_type: workFlowActionData?.action_config?.body_type || 'JSON',
        },
      });
    }
  }, [configModule, activeTab]);

  useEffect(() => {
    if (triggerType === 'Schedule') {
      const allOptions = [
        ...timeOptions,
        ...timeZoneOptions,
        ...daySelectionOptions,
        ...monthDaysOptions,
      ];
      const optionsMap = Object?.fromEntries(
        allOptions?.map((option) => [option.id, option.value]),
      );
      const updatedTriggerConfig = Object.fromEntries(
        Object.entries(selectedValue).map(([key, id]) => {
          if (Object.prototype.hasOwnProperty.call(optionsMap, Number(id))) {
            return [key, optionsMap[Number(id)]];
          }
          return [key, id];
        }),
      );
      setWorkFlowData((prevState: workFlowDataType & { workFlowData?: workFlowDataType }) => ({
        ...prevState,
        workFlowData: {
          ...prevState.workFlowData,
          trigger_config: {
            ...updatedTriggerConfig,
          },
        },
      }));
    } else if (triggerType === 'Webhook API') {
      setWorkFlowData((prevState: workFlowDataType & { workFlowData?: workFlowDataType }) => ({
        ...prevState,
        workFlowData: {
          ...prevState.workFlowData,
          trigger_config: {
            ...prevState.workFlowData?.trigger_config,
          },
        },
      }));
    }
  }, [selectedValue, triggerType]);

  useEffect(() => {
    const parsedActions = JSON.parse(savedValue!)?.actions;
    if (!parsedActions) return;

    const matchingAction = parsedActions?.find(
      (action: { action_order: number; action_id: number }) =>
        [action.action_order, action.action_id].includes(Number(selectedNode?.id)) ||
        action.action_id === selectedNode?.data?.unique_id,
    );

    setWorkFlowActionData((prev) => ({
      ...prev,
      action_config: { ...(prev.action_config ?? []) },
    }));

    if (matchingAction?.action_config) {
      const {
        headers = {},
        query_params: queryParams = {},
        body = {},
      } = matchingAction?.action_config ?? [];

      setUpdatedWorkflowPanelData((prev) => ({
        ...(prev ?? []),
        [triggerType]: {
          ...(prev[triggerType as keyof WorkflowFields] ?? []),
          configuration: prev[triggerType as keyof WorkflowFields].configuration.map(
            (field: Fields) => {
              const fieldMapping: Record<string, string> = {
                headers,
                queryParams,
                body,
              };
              if (field?.props?.name)
                if (field?.props?.name in fieldMapping) {
                  const entries = Object.entries(fieldMapping[field.props.name]);
                  return {
                    ...field,
                    keyValuePairs:
                      entries.length > 0
                        ? entries?.map(([key, value]) => ({ key, value }))
                        : [{ key: '', value: '' }],
                  };
                }
              return field;
            },
          ),
        },
      }));
    }
  }, [savedValue, selectedNode]);

  // render previous node data
  useEffect(() => {
    if (selectedNode?.data?.node_type === 'action') {
      const savedDataValue = localStorage?.getItem('workFlowData');
      const parsedSavedValue = savedDataValue ? JSON.parse(savedDataValue) : {};
      const selectActionConfig = parsedSavedValue?.actions?.findIndex(
        (item: { action_order: string; action_id: number }) =>
          item.action_order === selectedNode.id || item.action_id === selectedNode.data.unique_id,
      );
      const actionType: number = selectActionConfig - 1;
      const previousData =
        actionType === -1 ? parsedSavedValue?.trigger : parsedSavedValue?.actions[actionType];

      const wfId =
        workflowId ||
        workFlowData?.workflow_id ||
        JSON.parse(localStorage.getItem('workFlowData') || '{}')?.trigger?.workflow_id;

      if (actionType === -1) {
        getPreviousNodeTriggerData({
          workflow_id: wfId,
          trigger_id: previousData?.id || previousData?.trigger_id,
        })
          .then((res) => {
            setDynamicResponses(getPreviousResponseKeys(res.data?.[0]?.last_response));
          })
          .catch(() => makeToast('error', 'Error in getting previous node data'));
      } else {
        getPreviousNodeActionData({
          workflow_id: wfId,
          action_id: previousData?.action_id,
        })
          .then((res) => {
            setDynamicResponses(getPreviousResponseKeys(res.data?.data?.[0]?.last_response));
          })
          .catch(() => makeToast('error', 'Error in getting previous node data'));
      }
    }
  }, []);

  const handleSetUpContinueButton = () => {
    const savedJsonValue = localStorage?.getItem('workFlowData');
    const parsedValue = savedJsonValue ? JSON.parse(savedJsonValue) : {};
    if (triggerType === 'Schedule' || triggerType === 'Webhook API') {
      const triggerId = parsedValue?.trigger?.id || parsedValue?.trigger?.triggerId;
      const wfId =
        workflowId ||
        workFlowData?.workflow_id ||
        JSON.parse(localStorage.getItem('workFlowData') || '{}')?.trigger?.workflow_id;
      const data: {
        id?: number;
        workflow_id: number;
        trigger_catalogue_id: string;
        trigger_config: triggerConfigData;
      } = {
        workflow_id: wfId,
        trigger_catalogue_id: workFlowData?.trigger_catalogue_id || '',
        trigger_config:
          (workFlowData as workFlowDataType & { workFlowData?: workFlowDataType })?.workFlowData
            ?.trigger_config ?? {},
      };
      if (triggerId) {
        data.id = triggerId;
      }
      const dataToSaveInStorage = {
        triggerId,
        isSetUpTabCompleted: JSON.parse(savedValue!)?.trigger?.isSetUpTabCompleted,
        workflow_id: wfId,
        trigger_catalogue_id: workFlowData?.trigger_catalogue_id,
        workFlowData: {
          trigger_config: (workFlowData as workFlowDataType & { workFlowData?: workFlowDataType })
            ?.workFlowData?.trigger_config,
        },
      };
      postWorkFlowTrigger(data)
        .then((res) => {
          if (res.status === 201 || res.status === 200) {
            setTriggerId(res?.data?.data?.id);
            makeToast('success', res.data.message);
            localStorage.setItem(
              'workFlowData',
              JSON.stringify({
                ...((parsedValue?.actions?.length > 0
                  ? { actions: [...(parsedValue?.actions ?? [])] }
                  : null) ?? []),
                trigger: {
                  ...(dataToSaveInStorage ?? []),
                  trigger_id: res?.data?.data?.id,
                  isConfigTabCompleted: true,
                },
              }),
            );
            setActiveTab(2);
          }
          if (res?.status === 401) {
            setLogout(true);
          }
        })
        .catch(() => {
          makeToast('error', 'Something went wrong');
        });
    } else if (
      triggerType === 'Assistants' ||
      triggerType === 'Send Prompt' ||
      triggerType === 'send prompt' ||
      triggerType === 'API' ||
      triggerType === 'Send HTTP Request' ||
      triggerType === 'Gateways'
    ) {
      // JSON check
      if (
        triggerType === 'Gateways' ||
        triggerType === 'API' ||
        triggerType === 'Send HTTP Request'
      ) {
        let jsonString = '';

        if (typeof workFlowActionData?.action_config?.gateway_config === 'string') {
          jsonString = workFlowActionData?.action_config?.gateway_config;
        } else if (
          workFlowActionData?.action_config?.body_type === 'JSON' &&
          typeof workFlowActionData?.action_config?.raw === 'string'
        ) {
          jsonString = workFlowActionData?.action_config?.raw;
        }

        const isDataJson = validateJSON(jsonString);
        if (!isDataJson) {
          makeToast('error', 'Data provided in the text field is not JSON');
          return;
        }
      }
      postWorkFlowAction({
        ...workFlowActionData,
        order: Number(workFlowActionData?.action_order) ?? 0 - 1,
        id: (actionId || selectedNode?.data.unique_id) as string,
        workflow_id: workFlowActionData?.workflow_id,
      })
        .then((res) => {
          if (res.status === 201 || res.status === 200) {
            setActionId(res?.data?.data?.action_id);
            setNodes((prev) =>
              prev.map((node) => {
                if (node?.data?.node_type === 'action') {
                  // Find the matching action_id from the response based on the node's `id`
                  const actionMatch = node.id === selectedNode?.id;
                  if (actionMatch) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        unique_id: res?.data?.data?.action_id, // Map the specific action_id
                      },
                    };
                  }
                }
                // Leave other nodes unchanged
                return node;
              }),
            );
            setWorkFlowActionData((prev) => ({
              ...prev,
              action_id: res?.data?.data?.action_id,
              isConfigTabCompleted: true,
            }));
            makeToast('success', res.data.message);
            const selectActionConfigIndex = parsedValue?.actions?.findIndex(
              (item: { action_order: string; action_id: number }) =>
                item.action_order === selectedNode?.id ||
                (item.action_id && item.action_id === selectedNode?.data.unique_id),
            );
            const updatedActions = [...(parsedValue?.actions ?? [])];
            updatedActions[selectActionConfigIndex] = {
              ...(workFlowActionData ?? []),
              action_id: res?.data?.data?.action_id,
              isConfigTabCompleted: true,
            };
            localStorage.setItem(
              'workFlowData',
              JSON.stringify({
                ...parsedValue, // Preserve existing data
                actions: updatedActions,
              }),
            );
            setActiveTab(2); // Navigate to the next tab
          } else if (res?.status === 401) {
            setLogout(true);
          }
        })
        .catch(() => {
          makeToast('error', 'Something went wrong');
        });
    }
  };

  const handleValueFromStorage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const input = textareaRef.current || refInput.current;
    let newValue = value;

    if (input) {
      const cursorPosition = input.selectionStart || 0;
      const currentValue = input.value;
      newValue = currentValue.slice(0, cursorPosition) + value + currentValue.slice(cursorPosition);
    }

    switch (triggerType) {
      case 'API':
      case 'Send HTTP Request':
        setWorkFlowActionData((prev) => ({
          ...prev,
          action_config: {
            ...prev.action_config,
            raw: newValue,
          },
        }));
        break;
      case 'Send Prompt':
      case 'send prompt':
        setWorkFlowActionData((prev) => ({
          ...prev,
          action_config: {
            ...prev.action_config,
            human_prompt: newValue,
          },
        }));
        break;
      case 'Gateways':
        setWorkFlowActionData((prev) => ({
          ...prev,
          action_config: {
            ...prev.action_config,
            gateway_config: newValue,
          },
        }));
        break;
      default:
        setWorkFlowActionData((prev) => ({
          ...prev,
          action_config: {
            ...prev.action_config,
            [triggerType]: newValue,
          },
        }));
    }

    setOpenPayloadModal(false);
  };

  const handleConfigurationValue = (
    field: Fields,
    e: Event | React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement>,
  ) => {
    const value =
      (e?.target as HTMLTextAreaElement | HTMLInputElement | HTMLSelectElement)?.value ?? e;
    if (selectedNode?.data.node_type === 'action' && field.props.name) {
      setWorkFlowActionData((prev) => ({
        ...prev,
        action_config: {
          ...prev.action_config,
          [field.props.name as string]: value,
        },
      }));
    } else {
      setWorkFlowData((prevState: workFlowDataType & { workFlowData?: workFlowDataType }) => ({
        ...prevState,
        workFlowData: {
          ...prevState.workFlowData,
          trigger_config: {
            ...prevState?.workFlowData?.trigger_config,
            [field.props.name as string]: value,
          },
        },
      }));
    }
  };

  useEffect(() => {
    const isEmpty = (value: string) => value === null || value === undefined || value === '';

    const isDynamicVariablesEmpty = () =>
      !workFlowActionData?.action_config?.dynamic_variables ||
      Object.keys(workFlowActionData?.action_config?.dynamic_variables).length === 0;

    const isTriggerConfigInvalid = () =>
      allKeys?.some((key) => {
        const value = (workFlowData as workFlowDataType & { workFlowData?: workFlowDataType })
          ?.workFlowData?.trigger_config?.[key];
        return typeof value !== 'boolean' && isEmpty(value as string);
      });

    let disabled = false;

    switch (triggerType) {
      case 'Schedule':
        disabled = isTriggerConfigInvalid();
        break;
      case 'Assistant':
        disabled = isDynamicVariablesEmpty();
        break;
      case 'Send Prompt':
      case 'send prompt':
        disabled = isEmpty(workFlowActionData?.action_config?.human_prompt as string);
        break;
      case 'API':
      case 'Send HTTP Request':
        if (isEmpty(workFlowActionData?.action_config?.url as string)) {
          disabled = true;
        } else if (workFlowActionData?.action_config?.body_type === 'JSON') {
          disabled = isEmpty(workFlowActionData?.action_config?.raw as string);
        } else {
          disabled = false;
        }
        break;
      case 'Gateways':
        disabled = isEmpty(workFlowActionData?.action_config?.gateway_config as string);
        break;
      default:
        disabled = false;
    }

    setIsButtonDisabled(disabled);
  }, [workFlowActionData, workFlowData]);

  const handleCancelClick = () => {
    setOpenPayloadModal(false);
  };

  const getValue = (field: Fields) =>
    selectedNode?.data.node_type === 'action'
      ? workFlowActionData?.action_config?.[field.props.name as string]
      : (workFlowData as workFlowDataType & { workFlowData?: workFlowDataType })?.workFlowData
          ?.trigger_config?.[field.props.name as string];

  return (
    <>
      <div className="space-y-6">
        {updatedWorkflowPanelData[triggerType as TriggerType]?.configuration?.map(
          (field: Fields, index: number) => {
            let isVisible = true;
            if (field.dependency?.length) {
              field.dependency?.forEach(
                (dependent: { inputName: string; value: string | number }) => {
                  if (
                    workFlowActionData?.action_config?.[dependent?.inputName] === dependent?.value
                  )
                    isVisible = false;

                  allKeys.forEach((key: string) => {
                    if (key === dependent?.value) isVisible = false;
                  });
                },
              );
            }
            if (!field.dependency?.length || !isVisible) {
              return RenderConfig({
                field,
                index,
                value: getValue(field) as string,
                handleConfigurationValue,
                dynamicVariables: dynamicResponses,
                setOpenPayloadModal,
                textareaRef,
              });
            }
            return null;
          },
        )}
        <button
          type="button"
          className={`w-full py-2 rounded-lg ${isButtonDisabled ? 'bg-[#6B7280]' : 'bg-[#122961]'}`}
          disabled={isButtonDisabled || disableMode}
          onClick={() => handleSetUpContinueButton()}
        >
          <span className="text-white">Continue</span>
        </button>
      </div>
      <BasicModal
        className="w-[400px]"
        open={openPayloadModal}
        resetData={handleCancelClick}
        confirmButtonText="Continue"
        saveData={handleCancelClick}
      >
        <ul>
          {dynamicResponses?.length > 0 ? (
            dynamicResponses?.map((item: string) => (
              <li className="flex gap-2 items-center">
                <input type="radio" name="keys" value={item} onChange={handleValueFromStorage} />
                {item}
              </li>
            ))
          ) : (
            <span>No keys found!</span>
          )}
        </ul>
      </BasicModal>
    </>
  );
}

export default Configure;
